/**
 * Pilot gate for CDA opt-in UI + CRM calls.
 *
 * VUE_APP_CDA_OPTIN_USER_IDS (optional):
 * - unset → DEFAULT_CDA_OPTIN_USER_IDS from config/cdaOptIn
 * - explicit empty → disabled for everyone
 * - `*` or `all` → enabled for everyone
 * - comma-separated iresa ids → only those users
 *
 * Register has no userId yet → KeepInTouch stays hidden unless `*` / `all`.
 */

import { DEFAULT_CDA_OPTIN_USER_IDS } from '@/config/cdaOptIn'

const ALL_TOKENS = new Set(['*', 'all'])

const readRawAllowlist = (): string => {
  const fromWindow =
    typeof window !== 'undefined'
      ? (window as Window).env?.VUE_APP_CDA_OPTIN_USER_IDS
      : undefined
  const fromProcess = process.env.VUE_APP_CDA_OPTIN_USER_IDS
  const raw =
    fromWindow !== undefined && fromWindow !== null
      ? String(fromWindow)
      : fromProcess !== undefined
        ? String(fromProcess)
        : DEFAULT_CDA_OPTIN_USER_IDS
  return raw.trim()
}

const parseAllowlist = (): { all: boolean; ids: Set<string> } => {
  const raw = readRawAllowlist()
  if (!raw) return { all: false, ids: new Set() }

  const parts = raw
    .split(/[,;\s]+/)
    .map(p => p.trim())
    .filter(Boolean)

  if (parts.some(p => ALL_TOKENS.has(p.toLowerCase()))) {
    return { all: true, ids: new Set() }
  }

  return { all: false, ids: new Set(parts) }
}

/** True when CDA opt-in is rolled out to every user (`*` / `all`). */
export const isCdaOptInEnabledForAll = (): boolean => parseAllowlist().all

/**
 * True when this iresa userId may use CDA opt-in (Profile + post-register CRM).
 * Missing / empty userId → false unless allowlist is `*` / `all`.
 */
export const isCdaOptInEnabledForUser = (
  userId?: string | null
): boolean => {
  const { all, ids } = parseAllowlist()
  if (all) return true
  if (!userId) return false
  return ids.has(String(userId).trim())
}

export default isCdaOptInEnabledForUser
